/**
 * Chart Components
 * Reusable chart components using Recharts
 */

import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const COLORS = {
  primary: '#FF6A39',
  secondary: '#FFB399',
  success: '#22C55E',
  info: '#6B8CA8',
  warning: '#F59E0B',
  cream: '#FAF8F6',
  black: '#1A1A1A',
  blackLight: '#666666',
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-border-light">
        <p className="text-sm font-semibold text-black mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm text-black-lighter">
            <span style={{ color: entry.color }}>●</span> {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Transaction Trends Line Chart
export interface TransactionTrendsProps {
  data: Array<{
    date: string;
    transactions: number;
    b3tr: number;
  }>;
}

export const TransactionTrendsChart: React.FC<TransactionTrendsProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis
          dataKey="date"
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <YAxis
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          iconType="circle"
        />
        <Line
          type="monotone"
          dataKey="transactions"
          stroke={COLORS.primary}
          strokeWidth={2}
          dot={{ fill: COLORS.primary, r: 4 }}
          activeDot={{ r: 6 }}
          name="Transactions"
        />
        <Line
          type="monotone"
          dataKey="b3tr"
          stroke={COLORS.success}
          strokeWidth={2}
          dot={{ fill: COLORS.success, r: 4 }}
          activeDot={{ r: 6 }}
          name="Stars Distributed"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Customer Growth Area Chart
export interface CustomerGrowthProps {
  data: Array<{
    date: string;
    total: number;
    new: number;
  }>;
}

export const CustomerGrowthChart: React.FC<CustomerGrowthProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis
          dataKey="date"
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <YAxis
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
        <Area
          type="monotone"
          dataKey="total"
          stackId="1"
          stroke={COLORS.primary}
          fill={COLORS.primary}
          fillOpacity={0.6}
          name="Total Customers"
        />
        <Area
          type="monotone"
          dataKey="new"
          stackId="2"
          stroke={COLORS.success}
          fill={COLORS.success}
          fillOpacity={0.6}
          name="New Customers"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Hourly Patterns Bar Chart
export interface HourlyPatternsProps {
  data: Array<{
    hour: string;
    transactions: number;
  }>;
}

export const HourlyPatternsChart: React.FC<HourlyPatternsProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis
          dataKey="hour"
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <YAxis
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          tickLine={{ stroke: '#E8E8E8' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="transactions" fill={COLORS.primary} radius={[8, 8, 0, 0]} name="Transactions" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Tier Distribution Pie Chart
export interface TierDistributionProps {
  data: Array<{
    name: string;
    value: number;
    color: string;
  }>;
}

export const TierDistributionChart: React.FC<TierDistributionProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          outerRadius={90}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Ecosystem Apps Breakdown
export interface EcosystemBreakdownProps {
  data: Array<{
    app: string;
    users: number;
  }>;
}

export const EcosystemBreakdownChart: React.FC<EcosystemBreakdownProps> = ({ data }) => {
  const ECOSYSTEM_COLORS = ['#22C55E', '#6B8CA8', '#F59E0B'];

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis type="number" tick={{ fill: COLORS.blackLight, fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="app"
          tick={{ fill: COLORS.blackLight, fontSize: 12 }}
          width={70}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="users" fill={COLORS.success} radius={[0, 8, 8, 0]} name="Users">
          {data.map((_entry, index) => (
            <Cell key={`cell-${index}`} fill={ECOSYSTEM_COLORS[index % ECOSYSTEM_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default {
  TransactionTrendsChart,
  CustomerGrowthChart,
  HourlyPatternsChart,
  TierDistributionChart,
  EcosystemBreakdownChart,
};
